/**
 * The loading, not-found and error states.
 *
 * **There is no 404 design.** M4 records that, and this is the honest stand-in: the same
 * type and spacing as the rest of the site, a plain explanation, and a way back. It is
 * not pretending to be a designed page.
 *
 * A not-found page sets `noindex` and offers a link home. Without the first, a crawler
 * that follows a stale link happily indexes the apology; without the second, the visitor
 * is stuck.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applyHead } from '../lib/head';
import { useSiteOptional } from '../lib/site-context';

export default function StatusPage({
  title, body, notFound = false
}: { title: string; body: string; notFound?: boolean }) {
  // This renders in two places: inside the locale shell, and in App while the region
  // list is still loading - before any provider exists. Hence the optional context.
  const site = useSiteOptional();

  useEffect(() => {
    if (!notFound) return;
    applyHead({ title, description: body, noindex: true });
  }, [notFound, title, body]);

  return (
    <div className="weco-container flex min-h-[50vh] flex-col items-start justify-center gap-6 py-20">
      <div className="flex flex-col gap-3">
        <h1 className="text-h3 font-medium text-ink">{title}</h1>
        {body ? <p className="max-w-xl text-ink-muted">{body}</p> : null}
      </div>

      {notFound && site ? (
        <Link
          to={site.path('')}
          className="inline-flex h-[45px] items-center rounded-m bg-blue-dark-200 px-8 text-body text-white transition-colors hover:bg-blue-dark-300"
        >
          {site.t('page.home')}
        </Link>
      ) : null}
    </div>
  );
}
